import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import type { CheckoutState } from './checkoutTypes';
import type { MomoNetwork } from './utils/ghanaMomo';

export interface SchoolInvoicePaymentLinkDetail {
  token: string;
  school: {
    id: number;
    name: string;
  };
  invoice: {
    id: number;
    amount: string;
    dueDate: string;
    studentCount: number;
  };
  expiresAt: string;
  status: string;
  momoCheckout?: boolean;
}

export interface InitiateSchoolInvoicePaymentRequest {
  token: string;
  phoneNumber?: string;
  email?: string;
  network?: MomoNetwork;
  startAgain?: boolean;
}

export type InitiateSchoolInvoicePaymentResponse = Partial<CheckoutState> & {
  nextStep?: string;
  checkoutUrl: string | null;
  success: boolean;
};

export const SchoolInvoicePaymentLinkApi = createApi({
  reducerPath: 'SchoolInvoicePaymentLinkApi',
  baseQuery,
  endpoints: builder => ({
    getSchoolInvoicePaymentLink: builder.query<SchoolInvoicePaymentLinkDetail, string>({
      query: token => ({
        url: `/pay-invoice/${token}`,
      }),
    }),
    initiateSchoolInvoicePayment: builder.mutation<
      InitiateSchoolInvoicePaymentResponse,
      InitiateSchoolInvoicePaymentRequest
    >({
      query: ({ token, phoneNumber, email, network, startAgain }) => ({
        url: `/pay-invoice/${token}/initiate`,
        method: 'POST',
        body: {
          ...(phoneNumber ? { phoneNumber } : {}),
          ...(email ? { email } : {}),
          ...(network ? { network } : {}),
          ...(startAgain ? { startAgain } : {}),
        },
      }),
    }),
    confirmSchoolInvoicePaymentOtp: builder.mutation<
      CheckoutState,
      { token: string; otp: string }
    >({
      query: ({ token, otp }) => ({
        url: `/pay-invoice/${token}/confirm-otp`,
        method: 'POST',
        body: { otp },
      }),
    }),
    getSchoolInvoicePaymentCheckoutStatus: builder.query<CheckoutState, string>(
      {
        query: token => ({
          url: `/pay-invoice/${token}/status`,
        }),
        keepUnusedDataFor: 0,
        // A person is waiting on "did my money move?": never held by the outage hold.
        extraOptions: { bypassOutageHold: true },
      }
    ),
  }),
});

export const {
  useGetSchoolInvoicePaymentLinkQuery,
  useInitiateSchoolInvoicePaymentMutation,
  useConfirmSchoolInvoicePaymentOtpMutation,
  useLazyGetSchoolInvoicePaymentCheckoutStatusQuery,
} = SchoolInvoicePaymentLinkApi;
